import React, { useCallback, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { AisOrder } from 'types';
import { AisOrderState, OrderTypeText, PageKey } from 'enums';
import { Table } from 'components/table/table';
import { TableCellText } from 'components/table-cell/text-cell';
import { ReworkCtx } from 'components/rework-ctx/rework-ctx';
import {
  BreadcrumbsCtx,
  initialBreadcrumbs,
} from 'components/portfolio/breadcrumbs-ctx';
import { TableCellDate } from 'components/table-cell/date';
import { tableCellEnumFactory } from 'components/table-cell/enum-cell';
import {
  AdditionalRowComponentProps,
  TableColumn,
} from 'components/table/table-types';
import { useTableHook } from 'components/table/table-hook';

import { fetchMyOrders, useOrderStates } from './order-api';
import { VirtualizedTable } from 'components/virtualized-table/virtualized-table';
import { archivalUnitColumns } from './order-history-archival-units-columns';
import { downloadFile } from 'utils/download-file';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      margin: theme.spacing(2),
    },
    wrapper: {
      backgroundColor: '#FAFAFA',
      minHeight: 100,
      padding: '0px 20px',
    },
    historyDetailField: {
      display: 'flex',
      padding: '2px 5px',
      alignItems: 'center',
    },
    historyDetailFieldLabel: {
      width: 110,
      fontWeight: 500,
      textAlign: 'right',
      marginRight: 20,
    },
  })
);

export function TableCellLinkText({ data }: { data: string }) {
  return (
    <Link to="/portfolio/badatelske-listy">
      <Typography title={data} component="div" variant="body2">
        {data}
      </Typography>
    </Link>
  );
}

const headCells: TableColumn[] = [
  {
    id: 'ordinalNumber',
    label: 'Číslo objednávky',
    CellComponent: TableCellText,
  },
  {
    id: 'researchSheet_ordinalNumber',
    label: 'Badatelský list',
    CellComponent: TableCellLinkText,
  },
  {
    id: 'archivalUnitReference.archivalCollection.mark',
    label: 'Značka AS',
    CellComponent: TableCellText,
  },
  {
    id: 'created',
    label: 'Datum zadání',
    CellComponent: TableCellDate,
  },
  {
    id: 'state',
    label: 'Stav',
    CellComponent: tableCellEnumFactory(useOrderStates, false),
  },
];

export function OrderHistory() {
  const classes = useStyles();

  const { setBreadcrumbs } = useContext(BreadcrumbsCtx);
  const tableProps = useTableHook<AisOrder>({ fetchFunction: fetchMyOrders });

  useEffect(() => {
    setBreadcrumbs([
      ...initialBreadcrumbs.breadcrumbs,
      {
        title: 'Přehled objednávek',
        key: PageKey.PORTFOLIO_ORDER_HISTORY,
      },
    ]);
  }, [setBreadcrumbs]);

  return (
    <Grid container>
      <Grid item xs={12} className={classes.table}>
        <Table<AisOrder>
          {...tableProps}
          columns={headCells}
          expandableRows={true}
          AdditionalRowComponent={OrderHistoryDetail}
        />
      </Grid>
    </Grid>
  );
}

function OrderHistoryDetail(props: AdditionalRowComponentProps<AisOrder>) {
  const classes = useStyles();
  const data = props.data;

  const handleDownloadOriginal = useCallback(
    async (path?: string) => {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/api/order/${data.id}/origin-download${
          path ? `?path=${path}` : ''
        }`
      );
      const blob = await response.blob();
      downloadFile(blob, `${path ?? data.id}.zip`);
    },
    [data.id]
  );

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid item xs={12} sm={6}>
          Další informace
          <div style={{ marginTop: 10 }}>
            <OrderHistoryDetailField
              label="Popis zadání"
              value={data.description ?? ''}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          Stav objednávky
          <OrderHistoryDetailState aisOrder={data} />
        </Grid>
        {data.type && data.type !== 'BASIC' && (
          <Grid item xs={12} sm={6}>
            <div className={classes.historyDetailField}>
              <span className={classes.historyDetailFieldLabel}>
                Typ objednávky:
              </span>
              <span>{OrderTypeText[data.type]}</span>
            </div>
          </Grid>
        )}
        {data.filesExpiration && (
          <Grid item xs={12} sm={6}>
            Expirace archiválií pro původce:
            <Typography>
              {new Date(data.filesExpiration).toLocaleDateString('cs')}
            </Typography>
          </Grid>
        )}
        {(data.requestedAisIds?.length ?? 0) > 0 && (
          <Grid item xs={(data.files?.length ?? 0) > 0 ? 6 : 12}>
            <VirtualizedTable
              data={data.requestedAisIds ?? []}
              columns={[
                {
                  width: 300,
                  datakey: '',
                  name: 'Identifikátor',
                  CellComponent: (props) => {
                    return <>{props.rowData}</>;
                  },
                },
              ]}
              tableName="Seznam požadovaných ID"
            />
          </Grid>
        )}

        {data.archivalUnitPaths && data.archivalUnitPaths.length > 0 && (
          <Grid item xs={12}>
            <VirtualizedTable
              data={data.archivalUnitPaths ?? []}
              columns={[
                {
                  width: 300,
                  datakey: '',
                  name: 'ID souboru',
                  CellComponent: (props) => {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography>{props.rowData}</Typography>
                        <Button
                          onClick={() => handleDownloadOriginal(props.rowData)}
                        >
                          Stáhnout
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              tableName="Zpřístupněné originály"
              headerActionButton={
                <Button onClick={() => handleDownloadOriginal()}>
                  Stáhnout všechny
                </Button>
              }
            />
          </Grid>
        )}

        {(data.files?.length ?? 0) > 0 && (
          <Grid item xs={6}>
            <VirtualizedTable
              data={data.files ?? []}
              columns={[
                {
                  width: 500,
                  datakey: '',
                  name: 'ID souboru',
                  CellComponent: (props) => {
                    return (
                      <Link
                        to={`${process.env.PUBLIC_URL}/api/file/${props.rowData.id}`}
                      >
                        {props.rowData.id}
                      </Link>
                    );
                  },
                },
              ]}
              tableName="Soubory ke stažení"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <VirtualizedTable
            data={data.archivalUnits ?? []}
            columns={archivalUnitColumns}
            tableName="Archiválie"
          />
        </Grid>
      </Grid>
    </div>
  );
}

function OrderHistoryDetailField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  const classes = useStyles();
  return (
    <div className={classes.historyDetailField}>
      <span className={classes.historyDetailFieldLabel}>{label}:</span>
      <span>{value}</span>
    </div>
  );
}

function OrderHistoryDetailState({ aisOrder }: { aisOrder: AisOrder }) {
  const { handleAisOrder } = useContext(ReworkCtx);

  switch (aisOrder.state) {
    case AisOrderState.SUBMITTED:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Objednávka je zadaná a čeká na posouzení správcem.
        </Typography>
      );
    case AisOrderState.REWORKED:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Objednávka je doplněná a čeká na posouzení správcem.
        </Typography>
      );
    case AisOrderState.TO_REWORK:
      return (
        <Typography color="error">
          Objednávka byla vrácena a je vyžadováno její doplnění dle poznámky
          správce:
          <br />
          <b>{aisOrder.adminNote}</b>
          <br />
          Pro přepracování &nbsp;
          <Link
            to={'/portfolio/historie-objednavek/oprava'}
            onClick={() => handleAisOrder?.(aisOrder)}
          >
            klikněte zde.
          </Link>
        </Typography>
      );
    case AisOrderState.DENIED:
      return (
        <Typography color="error">
          Objednávka byla zamítnuta správcem z důvodu:
          <br />
          <b>"{aisOrder.adminNote}"</b>
        </Typography>
      );
    case AisOrderState.PROCESSING:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Objednávka je zpracovávána a čeká se na dokončení.
        </Typography>
      );
    case AisOrderState.PARTIALLY_READY:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Objednávka je částečně připravena.
        </Typography>
      );
    case AisOrderState.READY:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Objednávka je připravena.
        </Typography>
      );
    case AisOrderState.COMPLETED:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Objednávka byla úspěšne uzavřena.
        </Typography>
      );
    default:
      return <></>;
  }
}
